import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { color } from '@/lib/design-tokens';

import { flowRateTooltipDate } from '../details-rules';
import type { FlowRatePoint } from '../details-schemas';

import styles from './flow-rate-chart.module.scss';

export type FlowRateChartProps = {
  points: FlowRatePoint[];
  /** Exactly 6 evenly-spaced ticks from 0, per the AC's Y-axis rule. */
  ticks: number[];
  max: number;
  labelFor: (point: FlowRatePoint) => string;
  /** How many X labels recharts skips between the ones it draws — `flowRateLabelInterval`. */
  labelInterval: number;
};

// recharts takes plain colour strings, so the tokens are read from the TS source rather than
// inlined as hex — otherwise a token change silently stops applying to the chart
// (ARCHITECTURE.md §Styling: design tokens are the single source of truth).
const CHART_COLORS = {
  line: color.status.info,
  grid: color.surface.muted,
  axisText: color.text.muted,
  cursor: color.border.default,
  /** The hovered point is a white disc ringed in the line colour. */
  activeDot: color.surface.card,
} as const;

type Row = { label: string; value: number; at: string | null };

/** Dark tooltip: "15 Gallons" over the point's date, as the design shows. */
function ChartTooltip({ active, payload }: { active?: boolean; payload?: { payload: Row }[] }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipValue}>{row.value} Gallons</span>
      <span className={styles.tooltipDate}>
        {flowRateTooltipDate({ at: row.at, value: row.value, label: row.label })}
      </span>
    </div>
  );
}

/**
 * The flow-rate line, drawn as a filled area with a blue stroke (Figma 219:327388).
 *
 * The Y domain and ticks are handed in already computed so the axis rule lives in one tested
 * place; recharts is told the exact ticks rather than being left to pick its own.
 */
export function FlowRateChart({ points, ticks, max, labelFor, labelInterval }: FlowRateChartProps) {
  const rows: Row[] = points.map((point) => ({
    label: labelFor(point),
    value: point.value,
    at: point.at,
  }));

  return (
    <div className={styles.chart}>
      <ResponsiveContainer width="100%" height={240}>
        {/* The right margin keeps the final X label from being clipped by the card edge. */}
        <AreaChart data={rows} margin={{ top: 8, right: 24, bottom: 0, left: -12 }}>
          <defs>
            <linearGradient id="flowRateFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_COLORS.line} stopOpacity={0.35} />
              <stop offset="100%" stopColor={CHART_COLORS.line} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={CHART_COLORS.grid} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            interval={labelInterval}
            tick={{ fontSize: 12, fill: CHART_COLORS.axisText }}
            dy={8}
          />
          <YAxis
            domain={[0, max]}
            ticks={ticks}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 12, fill: CHART_COLORS.axisText }}
            width={40}
            // Whole numbers only, no repeated labels (§3).
            tickFormatter={(value: number) => String(Math.round(value))}
          />
          <Tooltip
            content={<ChartTooltip />}
            cursor={{ stroke: CHART_COLORS.cursor, strokeDasharray: '4 4' }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={CHART_COLORS.line}
            strokeWidth={2}
            fill="url(#flowRateFill)"
            dot={false}
            activeDot={{
              r: 5,
              fill: CHART_COLORS.activeDot,
              stroke: CHART_COLORS.line,
              strokeWidth: 2,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
